// Display ledger for the spec's CostEscalation_UserStories.md.
// Every "as / I want / so that" / acceptance line is verbatim from the source
// markdown so the demo audience reads the spec's words, not a paraphrase.
// Status / whereItLives / notes are added by the dashboard team.

/* eslint-disable max-len */

export type StoryStatus = 'built' | 'partial' | 'not-yet';
export type StoryPersona =
  | 'the Bid Lead'
  | 'the Reviewer'
  | 'PM'
  | 'SRM'
  | 'Analyst'
  | 'Client';

export interface UserStory {
  id: string;             // e.g. "1.7.1"; anonymized profiles display "F-NN" here
  phase: 1 | 2;
  epic: string;           // e.g. "1.7 Bid Output Deliverable"
  title: string;          // verbatim "Story X.Y.Z — Title"
  persona: StoryPersona;
  asLine: string;         // verbatim
  iWantLine: string;      // verbatim
  soThatLine: string;     // verbatim
  acceptance: string[];   // verbatim bullets
  traceability: string;   // the `[§...]` tag
  status: StoryStatus;
  whereItLives: string;   // dashboard team annotation
  notes?: string;         // dashboard team annotation
  /** Set on anonymized ledgers — original internal id (e.g. "1.7.5") so click
   *  handlers can still look up the story by its source-spec identifier. */
  originalId?: string;
}

/** Copy points the user-stories ledger pulls from the active profile. */
export interface UserStoriesLedgerCopy {
  /** Label inserted into the §24.4.5 reference inside story 2.3.1's
   *  `whereItLives` line. MEPPI uses '§24.4.5 basket math'; the demo profile
   *  uses 'indexed-basket math'. */
  basketReconcilerWhereLabel: string;
}

export interface UserStoriesLedgerOptions {
  copy: UserStoriesLedgerCopy;
  /** If true, replaces internal story IDs with anonymized F-NN labels and
   *  clears traceability so the ledger is marketing-safe. */
  anonymize: boolean;
}

/** Build the ledger array. The verbatim spec content is preserved; only the
 *  customer-specific clause label inside story 2.3.1's whereItLives is
 *  parameterized, and (when `anonymize` is true) the story IDs and
 *  traceability strings are scrubbed. */
export function buildUserStories(opts: UserStoriesLedgerOptions): UserStory[] {
  const raw: UserStory[] = [
  // ─── Epic 1.1 — Data Inputs ───────────────────────────────────────────────
  {
    id: '1.1.1', phase: 1, epic: '1.1 Data Inputs',
    title: 'Maintain a parts master',
    persona: 'the Bid Lead',
    asLine: 'the Bid Lead',
    iWantLine: 'to enter or refresh a list of parts (part number/SKU, supplier, comparable group, primary index, pass-through status, recovery mechanism, in-bid-scope flag)',
    soThatLine: 'the bid analysis runs against the parts I have actually identified as priority.',
    acceptance: [
      'Adding a new part row makes it appear in downstream Part Analysis automatically.',
      'A part can be flagged `InBidScope = FALSE` to exclude it from Bid Output without deleting it.',
      'PrimaryIndexID must reference an existing index in the Index Registry; invalid IDs are flagged.',
      'PassThroughStatus accepts `Yes`, `Partial`, `No` and drives the buffer-applied logic.',
      'Comparable-part grouping is optional but recognised when set.',
    ],
    traceability: '[§3-Input-Part, Supplier, ComparableGroup, Index, PassThrough, Recovery]',
    status: 'built',
    whereItLives: 'Inputs tab → Parts Master',
    notes: 'Secondary index dropdown added per gap G8.',
  },
  {
    id: '1.1.2', phase: 1, epic: '1.1 Data Inputs',
    title: 'Maintain purchase history',
    persona: 'the Bid Lead',
    asLine: 'the Bid Lead',
    iWantLine: 'to enter purchase events (PartID, purchase date, unit price, quantity, supplier)',
    soThatLine: 'historic price movement can be calculated per part or comparable group.',
    acceptance: [
      'Adding a purchase row updates that part\'s data-points and first/latest price automatically.',
      'Quantity is optional; if absent, historic movement falls back to unweighted CAGR.',
      'Multiple suppliers per part are allowed.',
      'Purchase dates outside the baseline–review window are still kept (used for trend, not movement).',
    ],
    traceability: '[§3-Input-PurchaseDate, Price, Quantity]',
    status: 'built',
    whereItLives: 'Inputs tab → Purchase History',
  },
  {
    id: '1.1.3', phase: 1, epic: '1.1 Data Inputs',
    title: 'Maintain the index registry',
    persona: 'Analyst',
    asLine: 'the SpendKey Analyst',
    iWantLine: 'a single source of truth for indices (ID, name, type Material/Labour, weights, notes)',
    soThatLine: 'parts and the forecast all join on the same keys.',
    acceptance: [
      'Adding an index row makes it selectable as PrimaryIndexID/SecondaryIndexID on Parts Master.',
      'Material weights and Labour weights each show a sum check that warns when they don\'t total 100%.',
      'The seed registry contains the six indices currently agreed with the client.',
    ],
    traceability: '[§3-Input-Index, §9-Indices-need-agreement]',
    status: 'built',
    whereItLives: 'Inputs tab → Index Registry',
    notes: 'Closed as gap G6 in the UX-review iteration.',
  },
  {
    id: '1.1.4', phase: 1, epic: '1.1 Data Inputs',
    title: 'Maintain historical index values',
    persona: 'Analyst',
    asLine: 'the SpendKey Analyst',
    iWantLine: 'to append actual index values over time (IndexID, period date, value, source)',
    soThatLine: 'the model can read the baseline and review values for index movement.',
    acceptance: [
      'Index baseline value = latest IndexHistory row on or before the BaselineDate.',
      'Index review value = latest IndexHistory row on or before the ReviewDate.',
      'Source field is captured for audit (e.g. BLS series ID).',
    ],
    traceability: '[§4-Calc-IndexMovement]',
    status: 'built',
    whereItLives: 'Inputs tab → Index History',
  },
  {
    id: '1.1.5', phase: 1, epic: '1.1 Data Inputs',
    title: 'Maintain comparable groups',
    persona: 'the Bid Lead',
    asLine: 'the Bid Lead',
    iWantLine: 'to define comparable-part groups',
    soThatLine: 'I can bundle parts where the final SKU isn\'t confirmed yet.',
    acceptance: [
      'Each group has an ID, name, and description.',
      'A part can reference at most one group.',
      'Group membership doesn\'t break per-part analysis — it\'s used for bundled trend views.',
    ],
    traceability: '[§2 comparable parts, §3-Input-ComparableGroup]',
    status: 'built',
    whereItLives: 'Inputs tab → Comparable Groups',
    notes: 'Closed as gap G7.',
  },
  {
    id: '1.1.6', phase: 1, epic: '1.1 Data Inputs',
    title: 'Configure bid context',
    persona: 'the Bid Lead',
    asLine: 'the Bid Lead',
    iWantLine: 'to set the bid name, baseline date, review date, forward months, and confidence multiplier on one Settings sheet',
    soThatLine: 'the whole engine reruns when the bid window or aggressiveness changes.',
    acceptance: [
      'Editing BaselineDate or ReviewDate re-annualises every index movement.',
      'ConfidenceMultiplier of 1.2 produces a buffer 20% higher than the default 1.0.',
      'Forward months drives the prorated $ buffer per part.',
    ],
    traceability: '[§3-Input-BaselineDate, ReviewDate; §4-Calc-Buffer]',
    status: 'built',
    whereItLives: 'Bid Buffer tab → Bid context card',
  },

  // ─── Epic 1.2 — Data Validity ─────────────────────────────────────────────
  {
    id: '1.2.1', phase: 1, epic: '1.2 Data Validity Filtering',
    title: 'Exclude single-point parts from trend',
    persona: 'the Bid Lead',
    asLine: 'the Bid Lead',
    iWantLine: 'parts with only one purchase point flagged and excluded from historic trend calculation',
    soThatLine: 'the buffer is not based on a non-trend.',
    acceptance: [
      'A part with one purchase row shows Trend confidence = Sparse and historic % p.a. blank/0.',
      'Bid Output counts these parts under "single purchase point" in the headlines.',
      'The part still appears in coverage and risk views — it\'s not deleted, just down-weighted.',
    ],
    traceability: '[§3-DataValidity]',
    status: 'built',
    whereItLives: 'Bid Buffer tab → Single purchase point KPI; Top 10 row chips ("single-point").',
    notes: 'Trend confidence surfaces as Sparse / Limited / Solid throughout the UI (story 1.7.3 family); the underlying engine enum is still Low / Medium / High for backwards compatibility.',
  },
  {
    id: '1.2.2', phase: 1, epic: '1.2 Data Validity Filtering',
    title: 'Configure trend confidence thresholds',
    persona: 'the Reviewer',
    asLine: 'the Reviewer',
    iWantLine: 'to set the minimum data points and minimum months of history that qualify as Limited/Solid confidence',
    soThatLine: 'we can defend the confidence labels to the client.',
    acceptance: [
      'Defaults are 5 points and 18 months for Solid; below that = Limited; below the minimums = Sparse.',
      'Thresholds live on the Settings sheet so changes flow through.',
    ],
    traceability: '[§3-DataValidity, §9-Indices-need-agreement]',
    status: 'built',
    whereItLives: 'Inputs tab → Trend confidence thresholds card',
    notes: 'Closed as gap G2.',
  },

  // ─── Epic 1.3 — Trend Assessment ──────────────────────────────────────────
  {
    id: '1.3.1', phase: 1, epic: '1.3 Trend Assessment',
    title: 'Compute historic % p.a. per part',
    persona: 'the Bid Lead',
    asLine: 'the Bid Lead',
    iWantLine: 'the annualised price movement (CAGR) between the first and latest purchase price',
    soThatLine: 'I can quantify how the part has actually moved.',
    acceptance: [
      'CAGR uses first price, latest price, and date span.',
      'If date span is below the minimum-months threshold, historic % p.a. is suppressed.',
      'Result is shown on Part Analysis next to the source data points.',
    ],
    traceability: '[§4-Calc-HistoricMovement]',
    status: 'built',
    whereItLives: 'Bid Buffer tab → Top 10 row chip "historic X%".',
  },
  {
    id: '1.3.2', phase: 1, epic: '1.3 Trend Assessment',
    title: 'Compare similarity across comparable parts',
    persona: 'the Bid Lead',
    asLine: 'the Bid Lead',
    iWantLine: 'to see whether parts in the same comparable group move similarly over time',
    soThatLine: 'I can decide whether the grouping is a valid proxy.',
    acceptance: [
      'A group view shows historic % p.a. per part in the group plus a group average.',
      'A part whose movement diverges materially from the group average is flagged for review.',
    ],
    traceability: '[§3-TrendAssessment]',
    status: 'built',
    whereItLives: 'Bid Buffer tab → Comparable group analysis card',
    notes: 'Closed as gap G3. Divergence threshold = 5pp.',
  },

  // ─── Epic 1.4 — Index Mapping & Movement ──────────────────────────────────
  {
    id: '1.4.1', phase: 1, epic: '1.4 Index Mapping & Movement',
    title: 'Map each part to a primary index',
    persona: 'the Bid Lead',
    asLine: 'the Bid Lead',
    iWantLine: 'to assign a PrimaryIndexID (and optional SecondaryIndexID) to each part',
    soThatLine: 'index-based movement can be calculated and coverage assessed.',
    acceptance: [
      'PrimaryIndexID is required for a part to count as "covered".',
      'SecondaryIndexID is optional and used in commentary only (Phase 1).',
    ],
    traceability: '[§3-Input-Index]',
    status: 'built',
    whereItLives: 'Inputs tab → Parts Master (Primary index + Secondary index dropdowns).',
  },
  {
    id: '1.4.2', phase: 1, epic: '1.4 Index Mapping & Movement',
    title: 'Compute index movement',
    persona: 'the Bid Lead',
    asLine: 'the Bid Lead',
    iWantLine: 'the annualised movement of each part\'s primary index between baseline and review',
    soThatLine: 'I can compare it to the part\'s own historic movement.',
    acceptance: [
      'Index movement = annualised change between BaselineDate and ReviewDate values.',
      'If either baseline or review value is missing, the cell shows blank, not an error.',
    ],
    traceability: '[§4-Calc-IndexMovement]',
    status: 'built',
    whereItLives: 'Bid Buffer tab → Top 10 row chip "index X%".',
  },
  {
    id: '1.4.3', phase: 1, epic: '1.4 Index Mapping & Movement',
    title: 'Compare price vs index movement',
    persona: 'the Bid Lead',
    asLine: 'the Bid Lead',
    iWantLine: 'Part Analysis to show historic % p.a. alongside index % p.a.',
    soThatLine: 'I can see at a glance whether the part is tracking the index.',
    acceptance: [
      'Both numbers shown side-by-side on Part Analysis.',
      'Commentary on Bid Output narrates whether parts broadly track their indices.',
    ],
    traceability: '[§4-Calc-PriceVsIndex]',
    status: 'built',
    whereItLives: 'Bid Buffer tab → Top 10 chips + Commentary card.',
  },

  // ─── Epic 1.5 — Risk Scoring ──────────────────────────────────────────────
  {
    id: '1.5.1', phase: 1, epic: '1.5 Risk Scoring',
    title: 'Composite risk score per part',
    persona: 'the Bid Lead',
    asLine: 'the Bid Lead',
    iWantLine: 'a 0–100 risk score per part composed of movement, data, coverage, and recovery sub-scores',
    soThatLine: 'parts can be ranked by overall exposure.',
    acceptance: [
      'Each sub-score is capped at 100.',
      'Weights live on Settings (default 40/20/20/20) and must sum to 100%.',
      'Risk bands: High ≥ 60, Medium 30–59, Low < 30.',
    ],
    traceability: '[§4-Calc-RiskScore]',
    status: 'built',
    whereItLives: 'Bid Buffer tab → Top 10 Risk column; Risk band breakdown card.',
    notes: 'Risk bands corrected from 70/40 to 60/30 per spec (gap G1).',
  },
  {
    id: '1.5.2', phase: 1, epic: '1.5 Risk Scoring',
    title: 'Configurable risk weights',
    persona: 'the Reviewer',
    asLine: 'the Reviewer',
    iWantLine: 'to reweight the four risk components from Settings',
    soThatLine: 'we can stress different views (e.g. give more weight to coverage gaps).',
    acceptance: [
      'Editing a weight cascades to every Part Analysis row immediately.',
      'A sum-check cell warns if weights don\'t total 100%.',
    ],
    traceability: '[§4-Calc-RiskScore]',
    status: 'built',
    whereItLives: 'Inputs tab → Risk-score weights card.',
  },

  // ─── Epic 1.6 — Cost Buffer ───────────────────────────────────────────────
  {
    id: '1.6.1', phase: 1, epic: '1.6 Cost Buffer',
    title: 'Suggested buffer % per part',
    persona: 'the Bid Lead',
    asLine: 'the Bid Lead',
    iWantLine: 'a suggested annual buffer percentage per part',
    soThatLine: 'I can apply it to component cost in the bid.',
    acceptance: [
      'Buffer % = max(historic % p.a., index % p.a.) × ConfidenceMultiplier.',
      'If trend confidence is Sparse, buffer falls back to index % p.a. only.',
    ],
    traceability: '[§4-Calc-Buffer]',
    status: 'built',
    whereItLives: 'Bid Buffer tab → Top 10 Buffer % column.',
  },
  {
    id: '1.6.2', phase: 1, epic: '1.6 Cost Buffer',
    title: 'Pass-through adjustment',
    persona: 'the Bid Lead',
    asLine: 'the Bid Lead',
    iWantLine: 'the $ buffer per part adjusted by pass-through status',
    soThatLine: 'recoverable risk doesn\'t get double-counted in the bid.',
    acceptance: [
      'PassThroughStatus = Yes → $ buffer is zeroed.',
      'PassThroughStatus = Partial → $ buffer is halved.',
      'PassThroughStatus = No → full $ buffer applied.',
    ],
    traceability: '[§4-Calc-PassThroughExposure]',
    status: 'built',
    whereItLives: 'Bid Buffer tab → Top 10 chip "pass-through: …".',
  },
  {
    id: '1.6.3', phase: 1, epic: '1.6 Cost Buffer',
    title: 'Prorate by forward window',
    persona: 'the Bid Lead',
    asLine: 'the Bid Lead',
    iWantLine: 'the $ buffer scaled to the forward months I\'m bidding for',
    soThatLine: 'a 6-month forward bid carries half the annual buffer.',
    acceptance: [
      '$ buffer per unit = latest price × buffer % × (ForwardMonths / 12) × pass-through factor.',
      'Total suggested buffer per part = $ per unit × annual quantity proxy.',
    ],
    traceability: '[§4-Calc-Buffer]',
    status: 'built',
    whereItLives: 'Bid Buffer tab → Top 10 Buffer $ column; Forward months on Bid context.',
  },
  {
    id: '1.6.4', phase: 1, epic: '1.6 Cost Buffer',
    title: 'Overall bid impact',
    persona: 'the Bid Lead',
    asLine: 'the Bid Lead',
    iWantLine: 'the total suggested buffer summed across in-scope parts',
    soThatLine: 'I have a single number to defend in the bid review.',
    acceptance: [
      'Total ignores out-of-scope parts.',
      'Bid Output also shows total as a % of total annual spend.',
    ],
    traceability: '[§4-Calc-OverallImpact]',
    status: 'built',
    whereItLives: 'Bid Buffer tab → hero KPI ($16,712 on seed); TOTAL row on Top 10.',
  },

  // ─── Epic 1.7 — Bid Output Deliverable ────────────────────────────────────
  {
    id: '1.7.1', phase: 1, epic: '1.7 Bid Output Deliverable',
    title: 'High-risk part summary',
    persona: 'the Bid Lead',
    asLine: 'the Bid Lead',
    iWantLine: 'a Top 10 highest-risk parts table on Bid Output',
    soThatLine: 'the review conversation focuses on the most material items.',
    acceptance: [
      'Ranks in-scope parts by risk score, descending.',
      'Shows part, supplier, primary index, trend confidence, risk score, buffer %, $ buffer.',
      'Updates as Parts Master / Purchase History change.',
    ],
    traceability: '[§5-HighRiskSummary]',
    status: 'built',
    whereItLives: 'Bid Buffer tab → Top 10 highest-risk parts card.',
    notes: 'Sortable on all columns.',
  },
  {
    id: '1.7.2', phase: 1, epic: '1.7 Bid Output Deliverable',
    title: 'Index coverage view',
    persona: 'the Bid Lead',
    asLine: 'the Bid Lead',
    iWantLine: 'to see, per index, how many in-scope parts use it and what spend/buffer it covers',
    soThatLine: 'I can spot which parts have no agreed index and price that gap in.',
    acceptance: [
      'Each index row: # parts, spend covered, buffer total.',
      '"Not covered" parts also called out as a count in the headline area.',
    ],
    traceability: '[§5-IndexCoverage]',
    status: 'built',
    whereItLives: 'Bid Buffer tab → Index coverage card + "Not covered by index" KPI.',
  },
  {
    id: '1.7.3', phase: 1, epic: '1.7 Bid Output Deliverable',
    title: 'Trend confidence headline counts',
    persona: 'the Bid Lead',
    asLine: 'the Bid Lead',
    iWantLine: 'counts of parts at Sparse, Limited, Solid confidence',
    soThatLine: 'I can flag where the bid is leaning on thin evidence.',
    acceptance: [
      'Counts shown alongside total parts in scope and parts with single purchase point.',
    ],
    traceability: '[§5-TrendConfidence]',
    status: 'built',
    whereItLives: 'Bid Buffer tab → Trend confidence KPI (3 pips: Sparse · Limited · Solid).',
    notes: 'Closed as gap G4. UI relabels Low/Medium/High to Sparse/Limited/Solid to avoid collision with the risk-band chip on the same row.',
  },
  {
    id: '1.7.4', phase: 1, epic: '1.7 Bid Output Deliverable',
    title: 'Plain-English commentary',
    persona: 'the Bid Lead',
    asLine: 'the Bid Lead',
    iWantLine: 'an auto-generated commentary paragraph using the headline numbers',
    soThatLine: 'I can paste it into the bid narrative without rewriting.',
    acceptance: [
      'Commentary references parts in scope, low-confidence count, not-covered count, total $ buffer, % of spend.',
      'Updates automatically when inputs change.',
    ],
    traceability: '[§5-Commentary]',
    status: 'built',
    whereItLives: 'Bid Buffer tab → Commentary card.',
  },
  {
    id: '1.7.5', phase: 1, epic: '1.7 Bid Output Deliverable',
    title: 'Total bid impact headline',
    persona: 'the Bid Lead',
    asLine: 'the Bid Lead',
    iWantLine: 'the total suggested buffer and buffer % of spend shown as the hero number',
    soThatLine: 'anyone opening the file sees the bottom line first.',
    acceptance: [
      'Top of Bid Output shows: parts in scope, total annual spend, TOTAL SUGGESTED BUFFER, buffer as % of spend.',
    ],
    traceability: '[§5-TotalBidImpact]',
    status: 'built',
    whereItLives: 'Bid Buffer tab → hero KPI strip.',
  },
  {
    id: '1.7.6', phase: 1, epic: '1.7 Bid Output Deliverable',
    title: 'Risk band breakdown',
    persona: 'the Reviewer',
    asLine: 'the Reviewer',
    iWantLine: 'counts by High/Medium/Low risk band',
    soThatLine: 'we can decide whether to add narrative for Medium-band items too.',
    acceptance: [
      'Three counts shown together; bands defined per Story 1.5.1.',
    ],
    traceability: '[§5-HighRiskSummary]',
    status: 'built',
    whereItLives: 'Bid Buffer tab → Risk band breakdown card.',
  },

  // ─── Epic 1.8 — Phase 1 Quality & Review ──────────────────────────────────
  {
    id: '1.8.1', phase: 1, epic: '1.8 Phase 1 Quality & Review',
    title: 'Sanity checks for review with the Bid Lead & the Reviewer',
    persona: 'the Reviewer',
    asLine: 'the Reviewer',
    iWantLine: 'built-in sanity-check cells (weight sums, registry row count, total revenue check)',
    soThatLine: 'we can verify the model end-to-end before the bid review.',
    acceptance: [
      'Weight sum checks on Index Registry both read 100%.',
      'Workbook recalcs end-to-end with zero formula errors.',
      'All Top-10 / coverage / commentary numbers reconcile to Part Analysis rows.',
    ],
    traceability: '[§7-Stage6]',
    status: 'built',
    whereItLives: 'Bid Buffer tab → Reconciliation card (after Commentary). Six checks: Total buffer, in-scope spend, parts count, Material weights, Labour weights, Risk-score weights. Red title + "Reconciliation failed" chip if any row fails.',
    notes: 'Dedicated Reconciliation card added with six tie-out rows. Weight sum checks mirrored from Inputs tab. All rows pass on seed data.',
  },
  {
    id: '1.8.2', phase: 1, epic: '1.8 Phase 1 Quality & Review',
    title: 'Data cleaning pass',
    persona: 'Analyst',
    asLine: 'the SpendKey Analyst',
    iWantLine: 'to identify records that are unusable (single point, missing date, missing price)',
    soThatLine: 'they\'re excluded or fixed before the bid review.',
    acceptance: [
      'A QA view lists parts with <2 purchase points, blank dates, or zero prices.',
      'The same view shows which parts have no PrimaryIndexID set.',
    ],
    traceability: '[§7-Stage2, §9-Not-all-records-usable]',
    status: 'built',
    whereItLives: 'Inputs tab → Data Quality card (auto-hidden when zero issues).',
    notes: 'Closed as gap G5.',
  },

  // ─── Epic 2.1 — Supplier Dashboard ────────────────────────────────────────
  {
    id: '2.1.1', phase: 2, epic: '2.1 Supplier Dashboard',
    title: 'Supplier-level rollup',
    persona: 'SRM',
    asLine: 'the SRM',
    iWantLine: 'a supplier-level view showing parts count, parts not covered, total spend, suggested buffer, buffer % of spend, avg historic % p.a., avg index % p.a.',
    soThatLine: 'I can have a focused conversation with each supplier.',
    acceptance: [
      'Unique suppliers auto-extracted from Parts Master.',
      'Each row reflects in-scope parts for that supplier only.',
      'Sortable by buffer %, spend, or risk.',
    ],
    traceability: '[§6-SupplierDashboard]',
    status: 'built',
    whereItLives: 'Supplier Dashboard tab → Exposure by supplier card.',
  },
  {
    id: '2.1.2', phase: 2, epic: '2.1 Supplier Dashboard',
    title: 'Drill-down to part level',
    persona: 'SRM',
    asLine: 'the SRM',
    iWantLine: 'to filter the dashboard down to one supplier and see all that supplier\'s parts with their risk and buffer',
    soThatLine: 'I can prepare specific evidence for a supplier meeting.',
    acceptance: [
      'Filter is a single dropdown or cell input.',
      'Drill-down shows the same columns as Top 10 but for the chosen supplier.',
    ],
    traceability: '[§6-SupplierDashboard]',
    status: 'built',
    whereItLives: 'Supplier Dashboard tab → Exposure by supplier rows are clickable; drill-down filters Bid Buffer Top 10. Row-click chosen over dropdown as a stronger affordance (both satisfy "single dropdown or cell input"). Selected row has left cyan border; active filter shown in heading chip.',
    notes: 'Row-click is a stronger affordance than a dropdown — interpreted as "cell input" per the acceptance bullet.',
  },
  {
    id: '2.1.3', phase: 2, epic: '2.1 Supplier Dashboard',
    title: 'Index mapping editor',
    persona: 'Analyst',
    asLine: 'the SpendKey Analyst',
    iWantLine: 'to maintain index mappings by supplier, part group, or material/labour driver',
    soThatLine: 'mappings can evolve as we agree more indices with the client.',
    acceptance: [
      'Edits flow through to all dependent calculations without manual fix-ups.',
      'A mapping change is timestamped (or flagged) for traceability.',
    ],
    traceability: '[§6-IndexMapping]',
    status: 'partial',
    whereItLives: 'Inputs tab → Parts Master (Primary/Secondary index dropdowns).',
    notes: 'Editing flows through live. Timestamped change log is not built.',
  },

  // ─── Epic 2.2 — Baseline & Review Dates ───────────────────────────────────
  {
    id: '2.2.1', phase: 2, epic: '2.2 Baseline & Review Dates',
    title: 'Define baseline and review period',
    persona: 'SRM',
    asLine: 'the SRM',
    iWantLine: 'to set the agreed baseline date and review date the dashboard uses',
    soThatLine: 'supplier conversations are anchored to the agreed period.',
    acceptance: [
      'Dates live on Settings (Phase 1 already supports this — Phase 2 surfaces them on the dashboard).',
      'Changing the review date re-pulls the index value used for comparison.',
    ],
    traceability: '[§6-Dates]',
    status: 'built',
    whereItLives: 'Bid Buffer tab → Bid context card (also visible in the page subtitle on every tab).',
  },

  // ─── Epic 2.3 — Price Increase Validation ─────────────────────────────────
  {
    id: '2.3.1', phase: 2, epic: '2.3 Price Increase Validation',
    title: 'Compare supplier claim vs index movement',
    persona: 'SRM',
    asLine: 'the SRM',
    iWantLine: 'to enter a supplier\'s claimed price increase and see how it compares to the agreed index movement over the same window',
    soThatLine: 'I can challenge or accept the claim with evidence.',
    acceptance: [
      'Inputs: supplier, part (or part group), current price, claim effective date.',
      'Output: index movement over same window, implied "justified" price, gap vs claim, verdict label (e.g. "Above index", "In line", "Below index").',
    ],
    traceability: '[§6-PriceIncreaseValidation]',
    status: 'partial',
    whereItLives: `Contract clause view → Negotiation reconciler (uses ${opts.copy.basketReconcilerWhereLabel}, not the supplier-specific window).`,
    notes: 'A supplier-specific price-claim validator with verdict label is not yet built in the parts-engine flow.',
  },
  {
    id: '2.3.2', phase: 2, epic: '2.3 Price Increase Validation',
    title: 'Save validation outcome',
    persona: 'SRM',
    asLine: 'the SRM',
    iWantLine: 'to log the outcome of each price-claim review (accepted / countered / pending)',
    soThatLine: 'we have a traceable record for the client.',
    acceptance: [
      'Log captures date, reviewer, supplier, part, claim %, agreed %, decision.',
      'Log is referenced from the supplier dashboard.',
    ],
    traceability: '[§6-Traceability]',
    status: 'not-yet',
    whereItLives: '—',
    notes: 'No validation log built yet.',
  },

  // ─── Epic 2.4 — Recovery Calculator ───────────────────────────────────────
  {
    id: '2.4.1', phase: 2, epic: '2.4 Escalation Recovery Calculator',
    title: 'Per-shipment recoverable escalation',
    persona: 'PM',
    asLine: 'a PM',
    iWantLine: 'to enter PartID, shipment date, base unit cost, units shipped on one line',
    soThatLine: 'the model returns the recoverable escalation = base cost × units × index movement.',
    acceptance: [
      'Index used is auto-pulled from Parts Master.',
      'Index baseline value = at BaselineDate; index shipment value = at shipment date.',
      'Index movement % is computed; recoverable $ = base cost × units × movement.',
      'A worked-example row matches scope ($100 × 7 × 5% = $35) to two decimals.',
    ],
    traceability: '[§6-RecoveryCalculator, scope example $100 × 7 × 5% = $35]',
    status: 'built',
    whereItLives: 'Recovery Calculator tab.',
  },
  {
    id: '2.4.2', phase: 2, epic: '2.4 Escalation Recovery Calculator',
    title: 'Total recovery roll-up',
    persona: 'PM',
    asLine: 'a PM',
    iWantLine: 'the calculator to sum recoverable escalation across all entered shipments',
    soThatLine: 'I have a single number for the customer invoice cycle.',
    acceptance: [
      'Totals row at the bottom of the calculator.',
      'Recovery total per part also visible on the supplier dashboard.',
    ],
    traceability: '[§6-RecoveryCalculator]',
    status: 'built',
    whereItLives: 'Recovery Calculator tab → TOTAL recoverable row. Supplier Dashboard tab → Exposure by supplier Recovery $ column. Per-supplier roll-up (story interpretation: per-supplier rather than per-part, as roll-up is what the dashboard can show).',
    notes: 'Recovery $ column added to Supplier Dashboard showing sum of recoverable $ from all lines for each supplier. Shows — (em dash) for suppliers with no recovery lines, $0.00 for lines summing to zero.',
  },
  {
    id: '2.4.3', phase: 2, epic: '2.4 Escalation Recovery Calculator',
    title: 'Recovery mechanism reference',
    persona: 'PM',
    asLine: 'a PM',
    iWantLine: 'to see the recovery mechanism (e.g. "Section 24.4 escalation") on each line',
    soThatLine: 'I attach the right contractual basis to the invoice.',
    acceptance: [
      'Pulled from Parts Master.',
      'Editable per line if the actual basis differs from the default.',
    ],
    traceability: '[§3-Input-Recovery, §6-RecoveryCalculator]',
    status: 'built',
    whereItLives: 'Parts Master Recovery column + Recovery Calculator per-line Recovery mechanism. Defaults from Parts Master when a part is selected; editable per line for overrides. CSV export includes the column.',
    notes: 'Recovery mechanism field added to Parts Master table (between Pass-through and In scope?). Recovery Calculator has an editable Recovery mechanism column per line, defaulting from the selected part.',
  },

  // ─── Epic 2.5 — Refresh & Traceability ────────────────────────────────────
  {
    id: '2.5.1', phase: 2, epic: '2.5 Refresh & Traceability',
    title: 'Refresh underlying data',
    persona: 'Analyst',
    asLine: 'the SpendKey Analyst',
    iWantLine: 'a documented, repeatable refresh process for Index History, Purchase History, and Parts Master',
    soThatLine: 'the dashboard can be re-run as conditions change.',
    acceptance: [
      'Refresh is described in the workbook README and runnable without breaking formulas.',
      'Long-form tables auto-extend so new rows are absorbed without formula edits.',
    ],
    traceability: '[§6-Refresh]',
    status: 'partial',
    whereItLives: 'Inputs tab → "Reset to seed data" button + every table\'s "+ Add" affordance.',
    notes: 'No documented batch-refresh process; live edits are immediate and persistent.',
  },
  {
    id: '2.5.2', phase: 2, epic: '2.5 Refresh & Traceability',
    title: 'Show assumptions and sources',
    persona: 'the Bid Lead',
    asLine: 'the Bid Lead / SRM / client',
    iWantLine: 'every calculation to be traceable to its source data, index selection, and assumption',
    soThatLine: 'outputs are defensible in commercial conversations.',
    acceptance: [
      'Each output cell ultimately traces to a named range or table column on a visible input sheet.',
      'Index history rows carry a Source field; mappings carry a Notes field.',
      'A "How this was calculated" note appears alongside each headline figure.',
    ],
    traceability: '[§6-Traceability]',
    status: 'built',
    whereItLives: 'Bid Buffer tab → each KPI card title has a (?) button; clicking opens a dedicated "How this was calculated" drawer below the KPI strip. Drawer shows formula, inputs with links, and a recompute log of the last 3 settings changes. Existing tooltips remain (additive).',
    notes: 'Dedicated "How this was calculated" drawer built for Total suggested buffer, In-scope annual spend, and Trend confidence KPIs. Index history rows carry Source field (built in 1.1.4); mappings carry Notes field (built in 1.1.3). Third acceptance bullet (Source + mappings Notes) confirmed still held.',
  },
  ];
  if (!opts.anonymize) return raw;
  return raw.map((story, i) => ({
    ...story,
    originalId: story.id,
    id: `F-${String(i + 1).padStart(2, '0')}`,
    traceability: '',
  }));
}

/** Backwards-compatible MEPPI-default export. Pre-existing consumers
 *  (the user-stories-ledger.spec.ts and any direct importer) keep their
 *  historical behaviour and the ledger stays byte-identical. */
export const USER_STORIES: UserStory[] = buildUserStories({
  copy: { basketReconcilerWhereLabel: '§24.4.5 basket math' },
  anonymize: false,
});

export const STORY_STATUS_LABEL: Record<StoryStatus, string> = {
  built: 'Built',
  partial: 'Partial',
  'not-yet': 'Not yet',
};

export const STORY_STATUS_TOOLTIP: Record<StoryStatus, string> = {
  built: 'Fully delivered in the live dashboard.',
  partial: 'Substantially delivered — see notes for what is still missing.',
  'not-yet': 'Identified but not yet built — recommended for the next iteration.',
};
